import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showSuccessModal } from '@Components/Modal/modalHelpers';
import OldOrderForm from '@Components/Order/OldOrderFormContainer/OldOrderForm';
import { TimeFormat } from '@Config/constants';
import { Messages } from '@Config/messages';
import { RouteConfig } from '@Config/routes';
import {
  AdvertFragment,
  AdvertPricingType,
  AdvertServiceType,
  CreateOrderMutationInput,
  CreateOrderMutationMutation,
  DeliveryType,
  OrderFragment,
  PaymentType,
  UpdateOrderMutationInput,
  UpdateOrderMutationMutation,
  useCreateOrderMutationMutation,
  useUpdateOrderMutationMutation,
} from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import { getCurrentLanguage } from '@Store/app/app.selectors';
import { locationChange } from '@Store/navigation/navigation.actions';
import { convertToFloatNumber } from '@Utils/convertions';
import { buildDateAndTime, formatDateOrTimeToString, getDateFromDateTime, getTimeFromDateTime } from '@Utils/dates';
import { callMutationForForm } from '@Utils/graphql';
import { OldOrderFormData } from '@Utils/types';

export interface OrderFormContainerProps {
  order?: OrderFragment;
  advert: AdvertFragment;
  onCancelClick?: () => void;
}

const OldOrderFormContainer: React.FunctionComponent<OrderFormContainerProps> = ({
  order,
  advert: { id, price, pricingType, deliveryPrice, deliveryType, serviceType, isScheduled, company },
  onCancelClick,
}) => {
  const isEditForm = !!order?.id;
  const dispatch = useDispatch();
  const t = useTranslations();
  const currentLanguage = useSelector(getCurrentLanguage);

  const [editOrderAction] = useUpdateOrderMutationMutation({
    onCompleted: () => {
      dispatch(showSuccessModal(t(Messages.btnOk), onCancelClick, t(Messages.orderEditSuccess)));
    },
  });

  const [createOrderAction] = useCreateOrderMutationMutation({
    onCompleted: () => {
      dispatch(
        showSuccessModal(
          t(Messages.btnOk),
          () => {
            dispatch(locationChange({ path: RouteConfig.MyOrders.buildLink() }));
          },
          t(Messages.orderCreateSuccess),
        ),
      );
    },
  });

  const buildInitialValues = (orderFragment: OrderFragment) => {
    return {
      dateFrom: getDateFromDateTime(orderFragment.startDate, currentLanguage),
      dateTo: getDateFromDateTime(orderFragment.endDate, currentLanguage),
      timeFrom: getTimeFromDateTime(orderFragment.startDate),
      timeTo: getTimeFromDateTime(orderFragment.endDate),
      amount: String(orderFragment.amount),
      deliveryIsRequired: orderFragment.includeDelivery,
      paymentType: orderFragment.paymentType,
    };
  };

  const buildInitialDefaultValues = () => {
    return {
      dateFrom: '',
      dateTo: '',
      timeFrom: '',
      timeTo: '',
      deliveryIsRequired: deliveryType === DeliveryType.Required,
    };
  };

  const buildTimeLine = (dateFrom?: string, dateTo?: string, timeFrom?: string, timeTo?: string) => {
    if (!(dateFrom && dateTo && timeFrom && timeTo)) {
      return null;
    }
    const dateTimeFrom = buildDateAndTime(dateFrom, timeFrom);
    const dateTimeTo = buildDateAndTime(dateTo, timeTo);
    return {
      startDate: formatDateOrTimeToString(dateTimeFrom, TimeFormat.mutationDateTimeFormat),
      endDate: formatDateOrTimeToString(dateTimeTo, TimeFormat.mutationDateTimeFormat),
    };
  };

  const initial = isEditForm && order ? buildInitialValues(order) : buildInitialDefaultValues();
  const isNegotiablePrice = pricingType === AdvertPricingType.NegotiablePrice;
  const isFreeItem = pricingType === AdvertPricingType.FreeItem;

  const buildFormValues = (values: OldOrderFormData) => {
    const { dateFrom, dateTo, timeFrom, timeTo, amount } = values;
    const isService = serviceType === AdvertServiceType.Service;
    return {
      ...(isService && buildTimeLine(dateFrom, dateTo, timeFrom, timeTo)),
      ...(amount !== undefined && { amount: convertToFloatNumber(amount) }),
      description: values.description,
      includeDelivery: values.deliveryIsRequired,
    };
  };

  const buildCreateFormValues = (values: OldOrderFormData): CreateOrderMutationInput => {
    return {
      ...buildFormValues(values),
      advert: id,
      paymentType: isNegotiablePrice || isFreeItem ? PaymentType.AgreedPrice : (values.paymentType as PaymentType),
    };
  };

  const buildUpdateFormValues = (values: OldOrderFormData): UpdateOrderMutationInput => {
    return {
      ...buildFormValues(values),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: order!.id,
      paymentType: isNegotiablePrice || isFreeItem ? PaymentType.AgreedPrice : values.paymentType,
    };
  };

  const handleEditSubmit = (values: OldOrderFormData) => {
    return callMutationForForm<UpdateOrderMutationMutation>(
      editOrderAction({
        variables: {
          input: buildUpdateFormValues(values),
        },
      }),
    );
  };

  const handleCreateSubmit = (values: OldOrderFormData) => {
    return callMutationForForm<CreateOrderMutationMutation>(
      createOrderAction({
        variables: {
          input: buildCreateFormValues(values),
        },
      }),
    );
  };

  const submitForm = (values: OldOrderFormData) => (isEditForm ? handleEditSubmit(values) : handleCreateSubmit(values));

  return (
    <OldOrderForm
      advertId={id}
      advertPrice={price}
      deliveryPrice={deliveryPrice}
      deliveryType={deliveryType}
      advertPricingType={pricingType}
      initialValues={initial}
      onSubmit={values => submitForm(values)}
      onCancelClick={onCancelClick || (() => {})}
      isEditForm={isEditForm}
      advertServiceType={serviceType}
      isScheduled={isScheduled}
      availablePaymentTypes={company.availablePaymentTypes}
    />
  );
};

export default OldOrderFormContainer;
